import { useQueryClient } from "@tanstack/react-query";
import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { useCallback, useMemo } from "react";
import { toRole } from "../schema/core.ts";
import type { SessionUser } from "../schema/index.ts";
import { queryKeys } from "./query-keys.ts";

/**
 * better-auth client for the Worker's `/api/auth/*` routes (same origin). The
 * role field mirrors the server config: optional and never accepted as input.
 */
const authClient = createAuthClient({
  baseURL: typeof window === "undefined" ? undefined : window.location.origin,
  plugins: [
    inferAdditionalFields({ user: { role: { type: "string", required: false, input: false } } }),
  ],
});

/** The fields of better-auth's session user that the kit reads. */
interface AuthUser {
  id: string;
  name: string;
  email: string;
  role?: string | null;
}

const toSessionUser = (user: AuthUser | undefined): SessionUser | null =>
  user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        role: toRole(user.role),
      }
    : null;

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const useSession = (): SessionState => {
  const { data, isPending, refetch } = authClient.useSession();
  const sessionUser = data?.user;
  const user = useMemo(() => toSessionUser(sessionUser), [sessionUser]);
  return { user, loading: isPending, refresh: refetch };
};

interface SessionSnapshot {
  data: { user: AuthUser } | null;
  isPending: boolean;
  isRefetching: boolean;
}

/** The store behind `authClient.useSession()`; better-auth types its atoms loosely. */
const sessionStore = authClient.$store.atoms.session as {
  subscribe: (listener: (state: SessionSnapshot) => void) => () => void;
};

/**
 * The signed-in user outside React (route loaders), once the session has settled:
 * a fetch or refetch in flight (just after signing in or out) is awaited, so the
 * answer never belongs to the previous account.
 */
export const loadSessionUser = (): Promise<SessionUser | null> => {
  const { promise, resolve } = Promise.withResolvers<SessionUser | null>();
  let settled = false;
  const unsubscribe = sessionStore.subscribe((state) => {
    if (settled || state.isPending || state.isRefetching) return;
    settled = true;
    resolve(toSessionUser(state.data?.user));
  });
  // nanostores calls the listener synchronously on subscribe, so stop listening only after
  // the promise settles rather than from inside the listener.
  void promise.then(unsubscribe);
  return promise;
};

/** better-auth's error object, narrowed to what the forms need. */
export interface AuthFailure {
  code?: string;
  status: number;
}

type AuthResult = { ok: true } | { ok: false; error: AuthFailure };

interface AuthActions {
  signIn: (input: { email: string; password: string }) => Promise<AuthResult>;
  signUp: (input: { name: string; email: string; password: string }) => Promise<AuthResult>;
  signOut: () => Promise<void>;
}

/** Sign in/up/out; progress (and admin data) belongs to the previous user, so drop it. */
export const useAuthActions = (): AuthActions => {
  const queryClient = useQueryClient();

  const resetUserData = useCallback(async (): Promise<void> => {
    queryClient.removeQueries({ queryKey: queryKeys.admin.all() });
    await queryClient.invalidateQueries({ queryKey: ["progress"] });
  }, [queryClient]);

  return useMemo<AuthActions>(
    () => ({
      signIn: async ({ email, password }): Promise<AuthResult> => {
        const { error } = await authClient.signIn.email({ email, password });
        if (error) return { ok: false, error: { code: error.code, status: error.status } };
        await resetUserData();
        return { ok: true };
      },
      signUp: async ({ name, email, password }): Promise<AuthResult> => {
        const { error } = await authClient.signUp.email({ name, email, password });
        if (error) return { ok: false, error: { code: error.code, status: error.status } };
        await resetUserData();
        return { ok: true };
      },
      signOut: async (): Promise<void> => {
        await authClient.signOut();
        await resetUserData();
      },
    }),
    [resetUserData],
  );
};
